/**
 * Sprint T-16.1 · Automation Center · DlqCard + NotificationIntentCard + EventRow.
 *
 * Compact display units used by DlqTab / IntentsTab / EventsTab. All are
 * dumb components (props-in / JSX-out) · actions delegated to parent.
 */
import React from "react";
import { AlertOctagon, RefreshCw, Trash2, Mail, MessageSquare, Send, Webhook, Bell, Activity } from "lucide-react";
import JsonPreview from "./JsonPreview";
import { RelativeTime, AbsoluteTime } from "./Formatters";
import { RetryBadge } from "./Badges";


// ============================================================
// DlqCard
// ============================================================

export function DlqCard({ entry, onRetry, onDiscard }) {
  const testId = `dlq-card-${entry.id}`;
  const reviewed = Boolean(entry.reviewed_at);
  return (
    <article
      data-testid={testId}
      className="flex flex-col gap-2 rounded-lg border border-red-500/30 bg-red-500/5 p-3"
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <AlertOctagon className="h-4 w-4 text-red-500" />
            <code data-testid={`${testId}-job`} className="font-mono text-xs font-semibold text-red-500">
              {entry.job_name}
            </code>
            <RetryBadge attempt={entry.attempts} testId={`${testId}-attempts`} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-[var(--ag-text-dim)]">
            <span>
              exec: <code className="font-mono">{(entry.execution_id || "").slice(0, 8)}</code>
            </span>
            <span>·</span>
            <span>
              first: <RelativeTime iso={entry.first_seen_at} />
            </span>
            <span>·</span>
            <span>
              moved: <RelativeTime iso={entry.moved_at} />
            </span>
          </div>
        </div>
        {reviewed && (
          <span className="rounded border border-slate-500/30 bg-slate-500/10 px-1.5 py-0.5 text-[9px] uppercase text-slate-400">
            {entry.action || "revisado"}
          </span>
        )}
      </header>

      <div className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)]/60 p-2 text-[10px]">
        <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">error</div>
        <div className="mt-0.5">
          <code data-testid={`${testId}-error-type`} className="font-mono text-red-500">
            {entry.last_error?.type || "?"}
          </code>
          <span className="ml-2 text-[var(--ag-text)]">
            {(entry.last_error?.message || "").slice(0, 180)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">input</div>
          <JsonPreview data={entry.input} maxChars={100} testId={`${testId}-input`} label="input" />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">stack</div>
          <JsonPreview
            data={entry.last_error?.stack || ""}
            maxChars={100}
            testId={`${testId}-stack`}
            label="stack trace"
          />
        </div>
      </div>

      <footer className="flex flex-wrap items-center justify-end gap-1 border-t border-[var(--ag-border)] pt-2">
        <button
          type="button"
          data-testid={`${testId}-retry-btn`}
          onClick={() => onRetry?.(entry)}
          disabled={reviewed}
          className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-500 hover:bg-emerald-500/20 disabled:opacity-40"
        >
          <RefreshCw className="h-3 w-3" /> Reintentar
        </button>
        <button
          type="button"
          data-testid={`${testId}-discard-btn`}
          onClick={() => onDiscard?.(entry)}
          disabled={reviewed}
          className="inline-flex items-center gap-1 rounded border border-slate-500/40 px-2 py-1 text-[10px] text-slate-400 hover:bg-[var(--ag-surface-2)] disabled:opacity-40"
        >
          <Trash2 className="h-3 w-3" /> Descartar
        </button>
      </footer>
    </article>
  );
}


// ============================================================
// NotificationIntentCard
// ============================================================

const CHANNEL_ICON = {
  email: Mail,
  sms: MessageSquare,
  whatsapp: Send,
  push: Bell,
  webhook: Webhook,
};

export function NotificationIntentCard({ intent }) {
  const Icon = CHANNEL_ICON[intent.channel] || Bell;
  const testId = `intent-card-${intent.id}`;
  return (
    <article
      data-testid={testId}
      className="flex flex-col gap-1.5 rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] p-2.5"
    >
      <header className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-[var(--ag-gold)]" />
          <span className="font-mono text-[10px] uppercase text-[var(--ag-gold)]">
            {intent.channel}
          </span>
          <span className="rounded border border-slate-500/30 bg-slate-500/10 px-1.5 py-0.5 text-[9px] uppercase text-slate-400">
            {intent.status || "pending"}
          </span>
        </div>
        <RelativeTime iso={intent.created_at} testId={`${testId}-time`} />
      </header>

      <div className="text-[11px]">
        <div className="truncate" title={intent.to}>
          <span className="text-[var(--ag-text-dim)]">para: </span>
          <span className="font-mono">{intent.to}</span>
        </div>
        {intent.subject && (
          <div className="mt-0.5 truncate text-[var(--ag-text)]" title={intent.subject}>
            {intent.subject}
          </div>
        )}
        <div className="mt-0.5 text-[10px] text-[var(--ag-text-dim)]">
          template: <code className="font-mono">{intent.template_id}</code>
        </div>
      </div>

      <div className="border-t border-[var(--ag-border)] pt-1">
        <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">context</div>
        <JsonPreview data={intent.context} maxChars={80} testId={`${testId}-ctx`} label="context" compact />
      </div>

      {intent.triggered_by_execution_id && (
        <div className="text-[9px] text-[var(--ag-text-dim)]">
          exec: <code className="font-mono">{intent.triggered_by_execution_id.slice(0, 8)}</code>
        </div>
      )}
    </article>
  );
}


// ============================================================
// EventRow
// ============================================================

function _eventIconPrefix(name) {
  if (!name) return Activity;
  if (name.startsWith("automation.job.failed") || name.startsWith("automation.job.dlq")) {
    return AlertOctagon;
  }
  return Activity;
}

export function EventRow({ event }) {
  const testId = `event-row-${event.id}`;
  const Icon = _eventIconPrefix(event.event_name);
  return (
    <li
      data-testid={testId}
      className="flex items-start gap-2 border-b border-[var(--ag-border)]/60 py-1.5"
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 text-[var(--ag-gold)]" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <code data-testid={`${testId}-name`} className="font-mono text-[11px] text-[var(--ag-text)]">
            {event.event_name}
          </code>
          <AbsoluteTime iso={event.emitted_at} testId={`${testId}-time`} />
        </div>
        <div className="mt-0.5 flex items-center gap-3 text-[9px] text-[var(--ag-text-dim)]">
          {event.emitted_by_execution_id && (
            <span>
              exec: <code className="font-mono">{event.emitted_by_execution_id.slice(0, 8)}</code>
            </span>
          )}
          {event.correlation_id && (
            <span>
              corr: <code className="font-mono">{event.correlation_id.slice(0, 8)}</code>
            </span>
          )}
        </div>
        {event.payload && Object.keys(event.payload).length > 0 && (
          <div className="mt-1">
            <JsonPreview data={event.payload} maxChars={120} testId={`${testId}-payload`} label={event.event_name} />
          </div>
        )}
      </div>
    </li>
  );
}
